import React, { useEffect, useState } from "react";
import {
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Select,
    MenuItem,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox
} from "@mui/material";
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from "@mui/icons-material";
import { client } from "../api/Api";

const Products = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [totalElements, setTotalElements] = useState(0);
    const [editProductId, setEditProductId] = useState(null);
    const [editProduct, setEditProduct] = useState({});
    const [categoryFilter, setCategoryFilter] = useState("0");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [loadingIds, setLoadingIds] = useState([]);
    const [search, setSearch] = useState("");
    const [hasImage, setHasImage] = useState('all'); // 'all' | 'yes' | 'no'
    const [hasDescription, setHasDescription] = useState('all');
    const [hasPrice, setHasPrice] = useState('all');
    const [refreshTick, setRefreshTick] = useState(0);

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        fetchProducts();
    }, [page, rowsPerPage, categoryFilter, search, hasImage, hasDescription, hasPrice, refreshTick]);

    const fetchCategories = async () => {
        try {
            const res = await client.get("/cats/list");
            setCategories(res.data);
        } catch (err) {
            console.error("Failed to load categories", err);
        }
    };

    const DEFAULT_IMAGE_PATH = "/img/img.png";

    function imgPreviewSrc(imageValue) {
        if (!imageValue) return process.env.PUBLIC_URL + DEFAULT_IMAGE_PATH;
        if (typeof imageValue !== 'string') return process.env.PUBLIC_URL + DEFAULT_IMAGE_PATH;
        const val = imageValue.trim();
        if (val.startsWith('http://') || val.startsWith('https://')) return val;
        if (val.startsWith('/images/')) return val;
        // assume stored ID without extension
        return `/api/images/${val}.png`;
    }

    const fetchProducts = async () => {
        try {
            const catAlias = categoryFilter || "0";
            const qParam = search && search.trim().length > 0 ? `&q=${encodeURIComponent(search.trim())}` : "";
            const imgParam = hasImage === 'all' ? '' : `&hasImage=${hasImage === 'yes'}`;
            const descParam = hasDescription === 'all' ? '' : `&hasDescription=${hasDescription === 'yes'}`;
            const priceParam = hasPrice === 'all' ? '' : `&hasPrice=${hasPrice === 'yes'}`;
            const res = await client.get(`/products/${catAlias}?page=${page}&size=${rowsPerPage}${qParam}${imgParam}${descParam}${priceParam}`);
            setProducts(res.data.products);
            setTotalElements(res.data.totalElements);
        } catch (err) {
            console.error("Failed to load products", err);
        }
    };

    const getImageUrl = (imagePath) => {
        if (!imagePath) return process.env.PUBLIC_URL + DEFAULT_IMAGE_PATH;

        // Default to uploads directory
        return `https://tikrai.com/api/images/${imagePath}`;
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = event => {
        setRowsPerPage(+event.target.value);
        setPage(0);
        setSelectedIds([]);
    };

    const handleEditClick = (product) => {
        setEditProductId(product.id);
        setEditProduct({ ...product });
        setDialogOpen(true);
    };
    const handleDialogClose = () => {
        setDialogOpen(false);
        setEditProductId(null);
        setEditProduct({});
    };

    const handleSave = async () => {
        // You need to provide an endpoint for updating the product
        try {
            await client.put(`/products/${editProduct.id}`, editProduct);
            setDialogOpen(false);
            setEditProductId(null);
            fetchProducts();
        } catch (error) {
            alert("Saving failed");
        }
    };

    const handleGenerateImage = async (productId) => {
        if (!productId) return;
        setLoadingIds(prev => [...prev, productId]);
        try {
            await client.post(`/products/${productId}/generate-image`);
            await fetchProducts();
        } catch (e) {
            console.error('Failed to generate image', e);
            alert('Nepavyko sugeneruoti iliustracijos');
        } finally {
            setLoadingIds(prev => prev.filter(id => id !== productId));
        }
    };

    const handleEditFieldChange = (field, value) => {
        console.log(field, value);
        setEditProduct(prev => ({ ...prev, [field]: value }));
    };

    const handleUploadImage = () => {
        const inp = document.createElement('input');
        inp.type = 'file';
        inp.accept = 'image/*';
        inp.onchange = async () => {
            const file = inp.files && inp.files[0];
            if (!file) return;
            const fd = new FormData();
            fd.append('image', file);
            try {
                const res = await client.post('/products/upload-image', fd, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                const imageUrl = res?.data?.imageUrl;
                if (imageUrl) {
                    setEditProduct(prev => ({ ...prev, image: imageUrl }));
                }
            } catch (e) {
                console.error('Image upload failed', e);
                alert('Image upload failed');
            }
        };
        inp.click();
    };

    const allCategoriesFlat = categories.flatMap(cat =>
        cat.descendants?.length
            ? [cat, ...cat.descendants]
            : [cat]
    );

    const allSelectedOnPage = products.length > 0 && products.every(p => selectedIds.includes(p.id));
    const someSelectedOnPage = products.some(p => selectedIds.includes(p.id));

    const handleToggleAll = (checked) => {
        if (checked) {
            setSelectedIds(Array.from(new Set([...selectedIds, ...products.map(p => p.id)])));
        } else {
            const idsOnPage = new Set(products.map(p => p.id));
            setSelectedIds(selectedIds.filter(id => !idsOnPage.has(id)));
        }
    };

    const handleToggleOne = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleNormalizeSelected = async () => {
        if (selectedIds.length === 0) return;
        try {
            await client.post('/admin/normalizeIds', selectedIds);
            await fetchProducts();
        } catch (e) {
            console.error('Failed to normalize', e);
            alert('Normalization failed');
        }
    };

    return (
        <Paper style={{ padding: 20, marginTop: 15 }}>
            <Typography variant="h5" gutterBottom>
                Manage Products
            </Typography>
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div>
                    <Typography variant="body2" style={{ marginRight: 8, display: "inline-block" }}>Filter by Category:</Typography>
                    <Select
                        value={categoryFilter}
                        onChange={e => {
                            setCategoryFilter(e.target.value);
                            setPage(0);
                            setSelectedIds([]);
                        }}
                        style={{ minWidth: 200 }}
                    >
                        <MenuItem value="0">All</MenuItem>
                        {allCategoriesFlat.map(cat => (
                            <MenuItem key={cat.alias || cat.id} value={cat.alias || cat.id}>{cat.name}</MenuItem>
                        ))}
                    </Select>
                </div>
                <TextField
                    label="Filter by name"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                    size="small"
                    style={{ minWidth: 220 }}
                />
                <div>
                    <Typography variant="body2" style={{ marginRight: 8, display: "inline-block" }}>Has image:</Typography>
                    <Select
                        value={hasImage}
                        onChange={(e) => { setHasImage(e.target.value); setPage(0); }}
                        size="small"
                        style={{ minWidth: 120 }}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                    </Select>
                </div>
                <div>
                    <Typography variant="body2" style={{ marginRight: 8, display: "inline-block" }}>Has description:</Typography>
                    <Select
                        value={hasDescription}
                        onChange={(e) => { setHasDescription(e.target.value); setPage(0); }}
                        size="small"
                        style={{ minWidth: 120 }}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                    </Select>
                </div>
                <div>
                    <Typography variant="body2" style={{ marginRight: 8, display: "inline-block" }}>Has price:</Typography>
                    <Select
                        value={hasPrice}
                        onChange={(e) => { setHasPrice(e.target.value); setPage(0); }}
                        size="small"
                        style={{ minWidth: 120 }}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                    </Select>
                </div>
                <Button variant="outlined" onClick={() => { setPage(0); }}>
                    Filtruoti
                </Button>
                <div style={{ flex: 1 }} />
                <Button variant="contained" color="primary" disabled={selectedIds.length === 0} onClick={handleNormalizeSelected}>
                    Normalize selected ({selectedIds.length})
                </Button>
            </div>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    indeterminate={someSelectedOnPage && !allSelectedOnPage}
                                    checked={allSelectedOnPage}
                                    onChange={(e) => handleToggleAll(e.target.checked)}
                                />
                            </TableCell>
                            <TableCell>Picture</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Price</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {products.map(product => (
                            <TableRow key={product.id} hover selected={selectedIds.includes(product.id)}>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={selectedIds.includes(product.id)}
                                        onChange={() => handleToggleOne(product.id)}
                                    />
                                </TableCell>
                                <TableCell>
                                    <img
                                        onClick={() => !loadingIds.includes(product.id) && handleGenerateImage(product.id)}
                                        title={loadingIds.includes(product.id) ? 'Generuojama...' : 'Sugeneruoti iliustraciją'}
                                        src={imgPreviewSrc(product.image)}
                                        alt={product.name}
                                        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = process.env.PUBLIC_URL + '/img/img.png'; }}
                                        style={{
                                            width: 48,
                                            height: 48,
                                            objectFit: "cover",
                                            borderRadius: 4,
                                            cursor: 'pointer',
                                            opacity: loadingIds.includes(product.id) ? 0.5 : 1,
                                            transition: 'opacity 0.2s'
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    {product.name}
                                </TableCell>
                                <TableCell>
                                    {product.categoryAlias || product.category?.name || "-"}
                                </TableCell>
                                <TableCell>
                                    {product.lastPrice ?? "-"}
                                </TableCell>
                                <TableCell>
                                    <IconButton onClick={() => handleEditClick(product)}>
                                        <EditIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={totalElements}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[20, 100, 1000]}
            />

            <Dialog open={dialogOpen} onClose={handleDialogClose}>
                <DialogTitle>Edit Product</DialogTitle>
                <DialogContent>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                            <TextField
                                margin="dense"
                                label="Name"
                                fullWidth
                                value={editProduct.name || ""}
                                onChange={e => handleEditFieldChange("name", e.target.value)}
                            />
                        </div>
                        <div>
                            <TextField
                                margin="dense"
                                label="Alias"
                                fullWidth
                                value={editProduct.alias || ""}
                                onChange={e => handleEditFieldChange("alias", e.target.value)}
                            />
                        </div>
                        <div>
                            <TextField
                                margin="dense"
                                label="Name (LT)"
                                fullWidth
                                value={editProduct.nameLT || ""}
                                onChange={e => handleEditFieldChange("nameLT", e.target.value)}
                            />
                        </div>
                        <div>
                            <TextField
                                margin="dense"
                                label="Name (EN)"
                                fullWidth
                                value={editProduct.nameEN || ""}
                                onChange={e => handleEditFieldChange("nameEN", e.target.value)}
                            />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <TextField
                                margin="dense"
                                label="Description (HTML)"
                                fullWidth
                                multiline
                                minRows={3}
                                value={editProduct.description || ""}
                                onChange={e => handleEditFieldChange("description", e.target.value)}
                            />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <TextField
                                margin="dense"
                                label="Description JSON"
                                fullWidth
                                multiline
                                minRows={3}
                                value={editProduct.descriptionJSON || ""}
                                onChange={e => handleEditFieldChange("descriptionJSON", e.target.value)}
                            />
                        </div>
                        <div>
                            <TextField
                                margin="dense"
                                label="Package size"
                                fullWidth
                                value={editProduct.packageSize || ""}
                                onChange={e => handleEditFieldChange("packageSize", e.target.value)}
                            />
                        </div>
                        <div>
                            <TextField
                                margin="dense"
                                label="Type"
                                fullWidth
                                value={editProduct.type || ""}
                                onChange={e => handleEditFieldChange("type", e.target.value)}
                            />
                        </div>
                        <div>
                            <TextField
                                margin="dense"
                                label="Price"
                                fullWidth
                                value={editProduct.lastPrice || ""}
                                type="number"
                                onChange={e => handleEditFieldChange("lastPrice", e.target.value)}
                            />
                        </div>
                        <div>
                            <Select
                                fullWidth
                                value={editProduct.categoryAlias || editProduct.category?.id || ""}
                                label="Category"
                                style={{ marginTop: 16 }}
                                onChange={e =>
                                    handleEditFieldChange(
                                        "categoryAlias",
                                        e.target.value
                                    )
                                }
                            >
                                {allCategoriesFlat.map(cat =>
                                    <MenuItem key={cat.id} value={cat.id}>
                                        {cat.name}
                                    </MenuItem>
                                )}
                            </Select>
                        </div>
                        <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 12, alignItems: 'center' }}>
                            {editProduct.image ? (
                                <img src={imgPreviewSrc(editProduct.image)} alt="preview" style={{ height: 80, borderRadius: 6, border: '1px solid #eee' }} onError={(e)=>{e.currentTarget.onerror = null; e.currentTarget.src = process.env.PUBLIC_URL + '/img/img.png';}} />
                            ) : null}
                            <TextField
                                margin="dense"
                                label="Image path or URL"
                                helperText="/images/.., ID be plėtinio arba pilnas URL"
                                fullWidth
                                value={editProduct.image || ""}
                                onChange={e => handleEditFieldChange("image", e.target.value)}
                            />
                            <Button variant="outlined" onClick={handleUploadImage}>Įkelti…</Button>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="secondary" startIcon={<CancelIcon />}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} color="primary" startIcon={<SaveIcon />}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default Products;